/**
 * Erinnerung ansehen, anpassen und versenden.
 *
 * Der Text kommt aus der Vorlage, ist aber änderbar: Wer den Vorgang führt,
 * weiß oft mehr als Kora – ein Gespräch von gestern, eine Zusage, ein Detail,
 * das die Vorlage nicht kennt. Deshalb geht die Mail erst hinaus, wenn ein
 * Mensch sie gesehen und freigegeben hat.
 *
 * Versendet wird aus dem Postfach des Versenders und mit seiner Signatur –
 * nicht mit der des Angemeldeten. Sonst ginge die Antwort des Kunden an die
 * falsche Person.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { useKora } from '../zustand';
import { Modal, KnopfPrimaer, KnopfSekundaer } from '../komponenten/Modal';
import type { Angebot, MailVorschau } from '../typen';

export function ErinnerungModal({
  angebot,
  onSchliessen,
  onFertig,
}: {
  angebot: Angebot;
  onSchliessen: () => void;
  onFertig: () => void;
}) {
  const { toastZeigen } = useKora();
  const [betreff, setBetreff] = useState('');
  const [text, setText] = useState('');
  const [fehler, setFehler] = useState('');

  const vorschau = useQuery({
    queryKey: ['mail-vorschau', angebot.id],
    queryFn: () => api.hole<MailVorschau>(`/angebote/${angebot.id}/mail-vorschau`),
  });

  /*
   * Erst füllen, wenn die Vorlage da ist – und danach nicht mehr anfassen,
   * sonst würde eine Änderung des Benutzers beim nächsten Rendern überschrieben.
   */
  useEffect(() => {
    if (vorschau.data && !betreff && !text) {
      setBetreff(vorschau.data.betreff);
      setText(vorschau.data.text);
    }
  }, [vorschau.data, betreff, text]);

  const senden = useMutation({
    mutationFn: () =>
      api.sende<{ toast: string; trockenlauf: boolean }>(`/angebote/${angebot.id}/erinnerung`, {
        betreff: betreff.trim(),
        text: text.trim(),
      }),
    onSuccess: (a) => {
      toastZeigen(a.toast);
      onFertig();
      onSchliessen();
    },
    onError: (f) => setFehler(f instanceof Error ? f.message : 'Versand fehlgeschlagen.'),
  });

  const d = vorschau.data;

  return (
    <Modal
      titel="Erinnerung versenden"
      untertitel={angebot.name}
      breite={640}
      abstandOben={54}
      fest={senden.isPending}
      onSchliessen={onSchliessen}
      fuss={
        <>
          <KnopfSekundaer onClick={onSchliessen} disabled={senden.isPending}>
            Abbrechen
          </KnopfSekundaer>
          <KnopfPrimaer
            onClick={() => senden.mutate()}
            disabled={senden.isPending || !betreff.trim() || !text.trim() || !d}
          >
            {senden.isPending ? 'Wird versendet …' : 'Senden'}
          </KnopfPrimaer>
        </>
      }
    >
      {vorschau.isLoading && <div className="modal-hinweis">Vorlage wird geladen …</div>}

      {d && (
        <>
          <div className="modal-hinweis">
            Geht aus dem Postfach von <strong>{d.versender}</strong> an{' '}
            <strong>{angebot.kunde.mail || 'keine Adresse hinterlegt'}</strong>, mit seiner Signatur.
          </div>

          <label className="modal-feld">
            <span>Betreff</span>
            <input
              className="modal-eingabe"
              value={betreff}
              onChange={(e) => {
                setBetreff(e.target.value);
                setFehler('');
              }}
            />
          </label>

          <label className="modal-feld">
            <span>Text</span>
            <textarea
              className="modal-eingabe erinnerung-text"
              rows={14}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                setFehler('');
              }}
            />
            <span className="modal-hinweis">
              Die Signatur des Versenders wird beim Senden angehängt – sie steht nicht im Text.
            </span>
          </label>
        </>
      )}

      {fehler && <div className="modal-fehler">{fehler}</div>}
    </Modal>
  );
}